import { useState, useRef, useCallback } from 'react'

const OTP_LENGTH = 6

export function useOtpInput() {
  const [code, setCode] = useState<string[]>(Array(OTP_LENGTH).fill(''))
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  const codeString = code.join('')
  const isComplete = codeString.length === OTP_LENGTH

  const resetCode = useCallback(() => {
    setCode(Array(OTP_LENGTH).fill(''))
    inputRefs.current[0]?.focus()
  }, [])

  const handleChange = useCallback((index: number, value: string) => {
    if (value && !/^\d$/.test(value)) return

    setCode((prev) => {
      const next = [...prev]
      next[index] = value
      return next
    })

    if (value && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus()
    }
  }, [])

  const handleKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace' && !code[index] && index > 0) {
        inputRefs.current[index - 1]?.focus()
      }
      if (e.key === 'ArrowLeft' && index > 0) {
        inputRefs.current[index - 1]?.focus()
      }
      if (e.key === 'ArrowRight' && index < OTP_LENGTH - 1) {
        inputRefs.current[index + 1]?.focus()
      }
    },
    [code]
  )

  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH)
    if (pastedData.length === OTP_LENGTH) {
      setCode(pastedData.split(''))
      inputRefs.current[OTP_LENGTH - 1]?.focus()
    }
  }, [])

  const setRef = useCallback((index: number, el: HTMLInputElement | null) => {
    inputRefs.current[index] = el
  }, [])

  return {
    code,
    codeString,
    isComplete,
    resetCode,
    handleChange,
    handleKeyDown,
    handlePaste,
    setRef
  }
}
